import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crosshair, MapTrifold, SlidersHorizontal } from "@phosphor-icons/react";
import MeetingMap from "../components/map/MeetingMap";
import LocationPermissionBanner from "../components/location/LocationPermissionBanner";
import { meetingService } from "../services/meetingService";
import { placeService } from "../services/placeService";
import { accountService } from "../services/accountService";
import { useGeolocation } from "../hooks/useGeolocation";
import Logo from "../components/Logo";

const DEFAULT_CENTER = { lat: 40.4168, lng: -3.7038 }; // Madrid fallback

export default function MapPage() {
  const navigate = useNavigate();
  const { position, locating, denied, usingSaved, locate } = useGeolocation();
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [meetings, setMeetings] = useState([]);
  const [places, setPlaces] = useState([]);
  const [radiusKm, setRadiusKm] = useState(25);

  useEffect(() => {
    if (position) {
      setCenter(position);
    }
  }, [position]);

  useEffect(() => {
    (async () => {
      const settings = await accountService.getSettings();
      if (settings?.searchRadiusKm) setRadiusKm(settings.searchRadiusKm);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const cappedRadius = Math.min(radiusKm, 100);
      const [list, nearbyPlaces] = await Promise.all([
        meetingService.listAround({
          lat: center.lat,
          lng: center.lng,
          radiusKm: cappedRadius,
        }),
        placeService
          .listNearby({
            latitude: center.lat,
            longitude: center.lng,
            radiusKm: cappedRadius,
            forMap: true,
          })
          .catch(() => []),
      ]);
      setPlaces(nearbyPlaces);
      setMeetings(meetingService.enrichWithPlaces(list, nearbyPlaces));
    })();
  }, [center, radiusKm]);

  const stats = useMemo(() => {
    const open = meetings.filter((m) => m.status === "open").length;
    const closed = meetings.length - open;
    return { open, closed, total: meetings.length };
  }, [meetings]);

  return (
    <div className="relative h-[100dvh] w-full" data-testid="map-page">
      {/* Top glass header */}
      <header
        className="absolute left-0 right-0 top-0 z-20 zx-glass border-b"
        style={{ borderColor: "rgba(255,255,255,0.08)" }}
      >
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3">
          <Logo size={22} />
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.65)]">
            <MapTrifold size={14} weight="thin" />
            {stats.open} abiertos · {stats.closed} cerrados
          </div>
        </div>
      </header>

      <MeetingMap
        center={center}
        meetings={meetings}
        places={places}
        userPosition={position}
        radiusKm={radiusKm}
        onMarkerClick={(m) => navigate(`/meetings/${m.id}`)}
      />

      {/* Bottom floating controls */}
      <div className="absolute bottom-[calc(5rem+env(safe-area-inset-bottom))] left-0 right-0 z-20 px-5">
        <div className="mx-auto max-w-3xl space-y-2">
          {denied && (
            <LocationPermissionBanner
              onRetry={() => locate({ preferSaved: false })}
              locating={locating}
              usingSaved={usingSaved && !!position}
            />
          )}

          <div className="zx-card flex items-center gap-3 p-3 px-4">
            <button
              onClick={() => locate({ preferSaved: false })}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(139,30,63,0.18)] ring-1 ring-[rgba(184,50,90,0.45)] transition-transform hover:scale-105"
              aria-label="Centrar en mi ubicación"
              data-testid="map-locate-button"
            >
              <Crosshair size={18} weight="thin" color="#e9b5c5" />
            </button>
            <div className="flex flex-1 items-center gap-3">
              <SlidersHorizontal size={14} weight="thin" />
              <div className="flex-1">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.55)]">
                  <span>Radio</span>
                  <span className="text-[#f5f5f5]">{radiusKm} km</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={100}
                  value={radiusKm}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setRadiusKm(v);
                    accountService.updateSettings({ searchRadiusKm: v });
                  }}
                  className="zx-range w-full accent-[#8B1E3F]"
                  data-testid="map-radius-input"
                />
              </div>
            </div>
            <button
              onClick={() => navigate("/meetings/new")}
              className="rounded-full bg-[#8B1E3F] px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-[#f5f5f5] hover:bg-[#B8325A]"
              data-testid="map-create-button"
            >
              + Crear
            </button>
          </div>

          {locating && (
            <p className="text-center text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.55)]">
              Localizando...
            </p>
          )}
          {denied && !position && (
            <p className="text-center text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.55)]">
              Sin geolocalización · vista por defecto
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
